import {
  Alert,
  Button,
  Avatar,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChipInput,
  ComboboxGrouped,
  type ComboboxGroupedItem,
  FieldError,
  Input,
  Select,
  SelectItem,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  Typography,
} from '@eye-able/ui';
import { ArrowLeft, CloudUpload, Plus, Save, Trash, X } from 'lucide-react';
import { resolveTeamColor } from '@pages/organisation/teams/teamColors';
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';
import type {
  CreatePolicyBody,
  PolicyAssetType,
  PolicyDomainScope,
  PolicyEntityScope,
  PolicyMatchMode,
  PolicyPriority,
  PolicyResponse,
  PolicyRuleGroup as PolicyRuleGroupBody,
  PolicyRuleQuantifier,
  PolicyRuleScope,
  PolicyStatus,
  PolicyViolationLabel,
} from '@http/queries/useCreatePolicy';
import { useFetchTeams } from '@http/queries';
import { useFetchPolicyDomainAccess } from '@http/queries/useFetchPolicyDomainAccess';
import PrioritySignal from '../PrioritySignal';
import type { PolicyTemplate } from './PolicyTemplateLibrary';
import { FormDomainSelect } from '@components/FormDomainSelect';
import { getInitials } from '@helpers/functions';
import { useUploadFiles } from '@/hooks/uploadFiles';
import { userAtom } from '@/global-store';
import { useAtomValue } from 'jotai';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export { getRuleFieldLabel, getRuleOperatorLabel } from './PolicyRuleLabels';

type RuleFieldOption = {
  value: string;
  label: string;
  operators: { value: string; label: string }[];
  placeholder: string;
};

type RuleRow = {
  id: number;
  field: string;
  operator: string;
  value: string;
};

export type RuleGroup = {
  id: number;
  /** What the group checks: the page itself, or each image / meta tag / file. */
  scope: PolicyRuleScope;
  /** How many items must satisfy the group; only meaningful when scoped. */
  quantifier: PolicyRuleQuantifier;
  matchMode: PolicyMatchMode;
  rules: RuleRow[];
};

export type RuleGroupRoot = {
  matchMode: PolicyMatchMode;
  groups: RuleGroup[];
};

type RuleFieldGroup = {
  label: string;
  options: RuleFieldOption[];
};

// `in-list` / `not-in-list` compare the whole field value against each list
// item with exact equality — right for enum-like fields, useless for prose.
const listOperators = ['in-list', 'not-in-list'];

function isListOperator(operator: string) {
  return listOperators.includes(operator);
}

// Phrase-list operators match ANY listed phrase inside the field, bounded by
// whole words, so a chip may hold a multi-word phrase ("machine learning").
// Their value is edited as chips and stored comma-joined, like the list operators.
const phraseListOperators = ['contains-words', 'not-contains-words'];

function isPhraseListOperator(operator: string) {
  return phraseListOperators.includes(operator);
}

const textOperators: RuleFieldOption['operators'] = [
  { value: 'starts-with', label: 'operatorStartsWith' },
  { value: 'ends-with', label: 'operatorEndsWith' },
  { value: 'contains', label: 'operatorContains' },
  { value: 'not-contains', label: 'operatorDoesNotContain' },
  { value: 'contains-words', label: 'operatorContainsPhrases' },
  { value: 'not-contains-words', label: 'operatorContainsNoPhrases' },
  { value: 'equal', label: 'operatorEqual' },
  { value: 'not-equal', label: 'operatorNotEqual' },
  { value: 'in-list', label: 'operatorInList' },
  { value: 'not-in-list', label: 'operatorNotInList' },
  {
    value: 'matches-blocked-terms-file',
    label: 'operatorMatchesBlockedTermsFile',
  },
  { value: 'regex', label: 'operatorRegex' },
];

// Long free-text fields: the whole text is never exactly equal to a short
// value, so the exact-equality operators (equal / list membership) are not
// offered.
const proseTextOperators: RuleFieldOption['operators'] = textOperators.filter(
  (operator) =>
    !isListOperator(operator.value) &&
    operator.value !== 'equal' &&
    operator.value !== 'not-equal',
);

const numberOperators: RuleFieldOption['operators'] = [
  { value: 'equal', label: 'operatorEqual' },
  { value: 'not-equal', label: 'operatorNotEqual' },
  { value: 'less-than', label: 'operatorLessThan' },
  { value: 'greater-than', label: 'operatorGreaterThan' },
  { value: 'in-list', label: 'operatorInList' },
  { value: 'not-in-list', label: 'operatorNotInList' },
];

const sizeOperators: RuleFieldOption['operators'] = [
  { value: 'equal', label: 'operatorEqual' },
  { value: 'not-equal', label: 'operatorNotEqual' },
  { value: 'less-than', label: 'operatorLessThan' },
  { value: 'greater-than', label: 'operatorGreaterThan' },
];

const ruleFieldGroups: RuleFieldGroup[] = [
  {
    label: 'ruleGroupPage',
    options: [
      {
        value: 'page-html',
        label: 'fieldPageHtml',
        operators: proseTextOperators,
        placeholder: 'placeholderEnterHtmlToMatch',
      },
      {
        value: 'page-url',
        label: 'fieldPageUrl',
        operators: textOperators,
        placeholder: 'placeholderEnterUrl',
      },
      {
        value: 'visible-text',
        label: 'fieldVisibleText',
        operators: proseTextOperators,
        placeholder: 'placeholderEnterPhraseToMatch',
      },
      {
        value: 'page-title',
        label: 'fieldPageTitle',
        operators: textOperators,
        placeholder: 'placeholderEnterTitleText',
      },
      {
        value: 'page-title-length',
        label: 'fieldPageTitleLength',
        operators: numberOperators,
        placeholder: 'placeholderEnterCharacterCount',
      },
    ],
  },
  {
    label: 'ruleGroupHeadings',
    options: [
      {
        value: 'h1-text',
        label: 'fieldH1Text',
        operators: textOperators,
        placeholder: 'placeholderEnterRequiredBrandOrPhrase',
      },
      {
        value: 'h2-text',
        label: 'fieldH2Text',
        operators: textOperators,
        placeholder: 'placeholderEnterRequiredBrandOrPhrase',
      },
      {
        value: 'h3-text',
        label: 'fieldH3Text',
        operators: textOperators,
        placeholder: 'placeholderEnterRequiredBrandOrPhrase',
      },
      {
        value: 'heading-length',
        label: 'fieldHeadingLength',
        operators: numberOperators,
        placeholder: 'placeholderEnterCharacterCount',
      },
    ],
  },
  {
    label: 'ruleGroupTypography',
    options: [
      {
        value: 'font-family-used',
        label: 'fieldFontFamilyUsed',
        operators: textOperators,
        placeholder: 'placeholderEnterFontFamilyName',
      },
      {
        value: 'font-family-count',
        label: 'fieldFontFamilyCount',
        operators: numberOperators,
        placeholder: 'placeholderEnterFontFamilyCount',
      },
      {
        value: 'font-weight-used',
        label: 'fieldFontWeightUsed',
        operators: textOperators,
        placeholder: 'placeholderEnterFontWeight',
      },
    ],
  },
  // The link fields (`link-text-length`, `external-link-count`) are not
  // offered for now — untested. The crawler and API still support them, so
  // re-enabling is just adding a 'Links' group back here.
];

// ---- Entity scopes -------------------------------------------------------
// A group either checks the page (each condition standalone) or one entity
// collection, where every condition is evaluated against the *same* item.
// Entity fields exist only inside their scope — that is what guarantees
// "filename ends with .png AND size > 500" describes one image. Keep in sync
// with the API's `POLICY_SCOPED_FIELDS` and the crawler's `SCOPED_FIELDS`.
type ScopeOption = {
  value: PolicyEntityScope;
  /** Noun used in the scope selector and the group summary ("image"). */
  noun: string;
  /** Group label shown in the field dropdown. */
  label: string;
  fields: RuleFieldOption[];
};

const scopeOptions: ScopeOption[] = [
  {
    value: 'image',
    noun: 'nounImage',
    label: 'scopeImage',
    fields: [
      {
        value: 'image-filename',
        label: 'fieldFilename',
        operators: textOperators,
        placeholder: 'placeholderEnterImageFilename',
      },
      {
        value: 'image-size',
        label: 'fieldSizeKb',
        operators: numberOperators,
        placeholder: 'placeholderEnterSizeKb',
      },
    ],
  },
  {
    value: 'meta-tag',
    noun: 'nounMetaTag',
    label: 'scopeMetaTag',
    fields: [
      {
        value: 'meta-header-name',
        label: 'fieldName',
        operators: textOperators,
        placeholder: 'placeholderEnterMetaHeaderName',
      },
      {
        value: 'meta-header-content',
        label: 'fieldContent',
        operators: textOperators,
        placeholder: 'placeholderEnterMetaHeaderContent',
      },
      {
        value: 'meta-length',
        label: 'fieldContentLength',
        operators: numberOperators,
        placeholder: 'placeholderEnterCharacterCount',
      },
    ],
  },
  {
    value: 'file',
    noun: 'nounFile',
    label: 'scopeFile',
    fields: [
      {
        value: 'file-size',
        label: 'fieldSizeMb',
        operators: sizeOperators,
        placeholder: 'placeholderEnterSizeMb',
      },
    ],
  },
];

const scopeOptionByValue = new Map(
  scopeOptions.map((scope) => [scope.value, scope]),
);

// The scope ("Check") selector is not user-facing yet: every group the
// builder creates checks the page. Scoped groups still arrive via templates
// or previously saved policies — they render their scope as a static label
// (tint and summary included) until the selector ships. Flip to expose it.
const SHOW_SCOPE_SELECT = false as boolean;

/** The scope an entity field belongs to; `page` for everything else. */
function scopeOfField(field: string): PolicyRuleScope {
  for (const scope of scopeOptions) {
    if (scope.fields.some((option) => option.value === field))
      return scope.value;
  }
  return 'page';
}

const ruleFieldOptions: RuleFieldOption[] = [
  ...ruleFieldGroups.flatMap((group) => group.options),
  ...scopeOptions.flatMap((scope) => scope.fields),
];

const defaultField = ruleFieldOptions[0];

/** The field a fresh condition starts from, per group scope. */
function firstFieldForScope(scope: PolicyRuleScope): RuleFieldOption {
  if (scope === 'page') return defaultField;
  return scopeOptionByValue.get(scope)?.fields[0] ?? defaultField;
}

/**
 * Templates carry a flat condition list; entity fields must live in their
 * scope, so conditions are split into one page group plus one `any`-quantified
 * group per referenced entity, all sharing the template's matchMode.
 */
function templateToRule(template: PolicyTemplate): RuleGroupRoot {
  let ruleId = 0;
  const rowsByScope = new Map<PolicyRuleScope, RuleRow[]>();
  for (const condition of template.conditions) {
    const field = condition.field;
    const fieldOption = getFieldOption(field);
    const row: RuleRow = {
      id: ++ruleId,
      field,
      operator: condition.operator || fieldOption.operators[0].value,
      value: condition.value,
    };
    const scope = scopeOfField(field);
    const rows = rowsByScope.get(scope);
    if (rows) rows.push(row);
    else rowsByScope.set(scope, [row]);
  }

  let groupId = 0;
  return {
    matchMode: 'all',
    groups: [...rowsByScope.entries()].map(([scope, rules]) => ({
      id: ++groupId,
      scope,
      quantifier: 'any',
      matchMode: template.matchMode,
      rules,
    })),
  };
}

// ---- Initial values ----------------------------------------------------
export type PolicyFormInitialValues = {
  name: string;
  description: string;
  assetType: PolicyAssetType;
  violationLabel: PolicyViolationLabel;
  priority: PolicyPriority;
  teamId: number | null;
  domainScope: PolicyDomainScope;
  auditEntryIds: number[];
  rule: RuleGroupRoot;
};

function emptyGroup(): RuleGroup {
  return {
    id: 1,
    scope: 'page',
    quantifier: 'any',
    matchMode: 'all',
    rules: [
      {
        id: 1,
        field: defaultField.value,
        operator: defaultField.operators[0].value,
        value: '',
      },
    ],
  };
}

/** A blank form, used by "New policy → From scratch". */
export function blankPolicyInitialValues(): PolicyFormInitialValues {
  return {
    name: '',
    description: '',
    assetType: 'html-pages',
    violationLabel: 'unwanted',
    priority: 'high',
    teamId: null,
    domainScope: 'all-domains',
    auditEntryIds: [],
    rule: {
      matchMode: 'all',
      groups: [emptyGroup()],
    },
  };
}

/** Prefill the form from a library template ("New policy → Use template"). */
export function templateInitialValues(
  template: PolicyTemplate,
  t: TFunction,
): PolicyFormInitialValues {
  return {
    name: t(template.nameKey),
    description: t(template.summaryKey),
    assetType: template.assetType,
    violationLabel: template.label,
    priority: template.priority,
    teamId: null,
    domainScope: 'all-domains',
    auditEntryIds: [],
    rule: templateToRule(template),
  };
}

/** Map an existing policy into form values (used by the Edit page). */
export function policyToInitialValues(
  policy: PolicyResponse,
): PolicyFormInitialValues {
  let ruleId = 0;
  let groupId = 0;
  const groups: RuleGroup[] = policy.rule.groups.map((group) => ({
    id: ++groupId,
    scope: group.scope,
    quantifier: group.quantifier ?? 'any',
    matchMode: group.matchMode,
    rules: group.rules.map((rule) => ({
      id: ++ruleId,
      field: rule.field,
      operator: rule.operator,
      value: rule.value,
    })),
  }));

  return {
    name: policy.name,
    description: policy.description ?? '',
    assetType: policy.assetType,
    violationLabel: policy.violationLabel,
    priority: policy.priority,
    teamId: policy.teamId,
    domainScope: policy.domainScope,
    auditEntryIds: policy.auditEntryIds,
    rule: {
      matchMode: policy.rule.matchMode,
      groups: groups.length > 0 ? groups : [emptyGroup()],
    },
  };
}

// ---- Validation --------------------------------------------------------
// The submit buttons are never disabled by form contents; pressing one
// validates with zod and surfaces inline errors instead of doing nothing.
const policyFormSchema = z
  .object({
    name: z.string().trim().min(1, 'policyNameRequired'),
    domainScope: z.enum(['all-domains', 'selected-domains']),
    includedDomains: z.array(z.number()),
    groups: z
      .array(
        z.object({
          rules: z
            .array(
              z.object({
                value: z.string().trim().min(1, 'conditionValueRequired'),
              }),
            )
            .min(1),
        }),
      )
      .min(1),
  })
  .superRefine((value, ctx) => {
    if (
      value.domainScope === 'selected-domains' &&
      value.includedDomains.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['includedDomains'],
        message: 'selectAtLeastOneDomain',
      });
    }
  });

type PolicyFormErrors = {
  name?: string;
  includedDomains?: string;
  rules?: string;
};

type RuleFieldItem = { value: string; label: string };

const groupedRuleFieldItems: ComboboxGroupedItem<RuleFieldItem>[] =
  ruleFieldGroups.map((group) => ({
    value: group.label,
    items: group.options.map((option) => ({
      value: option.value,
      label: option.label,
    })),
  }));

// Inside a scoped group the field dropdown only offers that entity's
// attributes; page fields are simply not representable there (and vice versa).
const scopedRuleFieldItems = scopeOptions.reduce(
  (items, scope) => {
    items[scope.value] = [
      {
        value: scope.label,
        items: scope.fields.map((option) => ({
          value: option.value,
          label: option.label,
        })),
      },
    ];
    return items;
  },
  {} as Record<PolicyEntityScope, ComboboxGroupedItem<RuleFieldItem>[]>,
);

function fieldItemsForScope(scope: PolicyRuleScope) {
  return scope === 'page' ? groupedRuleFieldItems : scopedRuleFieldItems[scope];
}

function getFieldOption(fieldValue: string) {
  return (
    ruleFieldOptions.find((option) => option.value === fieldValue) ??
    defaultField
  );
}

// Operators whose value is the URL of an uploaded term-list file (one term
// per line) rather than content to match, so they get an upload control
// instead of a text input.
const listFileOperators = ['matches-blocked-terms-file'];

function isListFileOperator(operator: string) {
  return listFileOperators.includes(operator);
}

// Example patterns shown as the value placeholder when the "Regex" operator
// is selected, tailored to the chosen field.
const defaultRegexPlaceholder = '^(term-one|term-two)\\b';

const regexPlaceholderByField: Record<string, string> = {
  'page-html': '<iframe[^>]*src="http://[^"]*"',
  'page-url': '^https://example\\.com/(blog|news)/.+',
  'visible-text': '\\b(cheap|free trial)\\b',
  'page-title': '\\| Example Corp$',
  'font-family-used': '^(Arial|Helvetica)$',
  'font-weight-used': '^[1-3]00$',
  'meta-header-name': '^og:(title|image)$',
  'meta-header-content': '^.{50,160}$',
  'image-filename': '\\.(png|jpe?g)$',
};

function getRegexPlaceholder(field: string) {
  return regexPlaceholderByField[field] ?? defaultRegexPlaceholder;
}

/** Upload control for list-file operators: uploads a plain-text term list
 * and stores the resulting file URL as the rule value. */
function ListFileValueInput({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange: (value: string) => void;
}) {
  const { t } = useInlineTranslation();
  const listFileExampleHref = `data:text/plain;charset=utf-8,${encodeURIComponent(
    t('blockedTermsExample'),
  )}`;
  const uploadListFile = useUploadFiles('governance/policy-lists');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>();

  const fileName = value
    ? decodeURIComponent(value.split('/').pop() ?? '')
    : '';

  const handleFileChange = async (file: File | undefined) => {
    if (!file) return;

    setIsUploading(true);
    setUploadError(undefined);
    try {
      onValueChange(await uploadListFile(file));
    } catch {
      setUploadError(t('uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className='eye:flex eye:flex-col eye:gap-5xs'>
      <input
        ref={fileInputRef}
        type='file'
        accept='.txt,text/plain'
        className='eye:hidden'
        onChange={(event) => {
          // Grab the File before resetting the input: `files` is a live
          // FileList that clearing the value empties.
          const file = event.target.files?.[0];
          // Reset so picking the same file again still triggers onChange.
          event.target.value = '';
          void handleFileChange(file);
        }}
      />
      <Button
        variant='outline'
        size='sm'
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        aria-label={
          fileName
            ? t('replaceBlockedTermsFile', { fileName })
            : t('uploadBlockedTermsFile')
        }
      >
        <CloudUpload aria-hidden />
        {isUploading ? t('uploading') : fileName || t('uploadBlockedTermsFile')}
      </Button>
      <Typography
        tag='a'
        variant='body-s'
        className='eye:self-start eye:underline eye:hover:no-underline'
        href={listFileExampleHref}
        download='blocked-terms-example.txt'
      >
        {t('downloadExampleFile')}
      </Typography>
      <FieldError>{uploadError}</FieldError>
    </div>
  );
}

/** Splits a comma-separated value the way the crawler does; list items and
 * phrases share the format, so a chip may contain spaces. */
function parseListValue(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function MatchConnector({
  value,
  onChange,
  ariaLabel,
}: {
  value: PolicyMatchMode;
  onChange: (mode: PolicyMatchMode) => void;
  ariaLabel: string;
}) {
  const { t } = useInlineTranslation();
  return (
    <div className='eye:flex eye:items-center eye:gap-xs eye:py-5xs'>
      <span className='eye:h-px eye:flex-1 eye:bg-grey-20' />
      <ToggleGroup
        type='single'
        size='sm'
        value={value}
        onValueChange={(next) => {
          if (next) onChange(next as PolicyMatchMode);
        }}
        aria-label={ariaLabel}
      >
        <ToggleGroupItem value='all'>{t('and')}</ToggleGroupItem>
        <ToggleGroupItem value='any'>{t('or')}</ToggleGroupItem>
      </ToggleGroup>
      <span className='eye:h-px eye:flex-1 eye:bg-grey-20' />
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
  action,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className='eye:pb-xs'>
        <div className='eye:flex eye:flex-col eye:gap-xs eye:lg:flex-row eye:lg:items-start eye:lg:justify-between'>
          <div className='eye:min-w-0'>
            <CardTitle>{title}</CardTitle>
            {description ? (
              <CardDescription>{description}</CardDescription>
            ) : null}
          </div>
          {action ? <div className='eye:shrink-0'>{action}</div> : null}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export type PolicyFormProps = {
  /** Page heading shown above the form. */
  heading: string;
  /** Sub-heading paragraph under the page heading. */
  description: string;
  /** Label of the primary (active) submit button, e.g. "Create policy". */
  submitLabel: string;
  /** Shows the secondary "Save as draft" button. Only a new policy can be a
   * draft; the edit page hides the button so an existing policy cannot be
   * saved back to draft. */
  canSaveAsDraft: boolean;
  /** Disables both submit buttons while a request is in flight. */
  isSubmitting: boolean;
  /** Values the form starts from (blank, from a template, or an existing policy). */
  initialValues: PolicyFormInitialValues;
  /** Called with a valid, built policy body once validation passes. */
  onSubmit: (body: CreatePolicyBody) => void;
};

function PolicyForm({
  heading,
  description: pageDescription,
  submitLabel,
  canSaveAsDraft,
  isSubmitting,
  initialValues,
  onSubmit,
}: PolicyFormProps) {
  const { t } = useInlineTranslation();
  const initialRuleIds = initialValues.rule.groups.flatMap((group) =>
    group.rules.map((rule) => rule.id),
  );
  const nextRuleIdRef = useRef(Math.max(0, ...initialRuleIds) + 1);
  const nextGroupIdRef = useRef(
    Math.max(0, ...initialValues.rule.groups.map((group) => group.id)) + 1,
  );

  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  // Asset type is not user-editable; it is submitted from the initial values
  // (html-pages for new policies, the stored value when editing).
  const assetType: PolicyAssetType = initialValues.assetType;
  // Neither is the violation label: new policies flag what they match
  // ("unwanted"), while templates and saved policies keep their stored label.
  const violationLabel: PolicyViolationLabel = initialValues.violationLabel;
  const [priority, setPriority] = useState<PolicyPriority>(
    initialValues.priority,
  );
  const [teamId, setTeamId] = useState<number | null>(initialValues.teamId);
  const user = useAtomValue(userAtom);
  const { data: teams = [] } = useFetchTeams(user?.primaryOrganisation, {
    enabled: true,
  });
  const { data: policyDomainAccess } = useFetchPolicyDomainAccess();

  const [domainScope, setDomainScope] = useState<PolicyDomainScope>(
    initialValues.domainScope,
  );
  const [includedDomainIds, setIncludedDomainIds] = useState<number[]>(
    initialValues.auditEntryIds,
  );
  const [rule, setRule] = useState<RuleGroupRoot>(initialValues.rule);
  const [errors, setErrors] = useState<PolicyFormErrors>({});

  const canEditAllDomains = policyDomainAccess?.canEditAllDomains !== false;

  useEffect(() => {
    if (!canEditAllDomains && domainScope === 'all-domains') {
      setDomainScope('selected-domains');
    }
  }, [canEditAllDomains, domainScope]);

  const totalRules = rule.groups.reduce(
    (count, group) => count + group.rules.length,
    0,
  );

  const createRule = (scope: PolicyRuleScope = 'page'): RuleRow => {
    const field = firstFieldForScope(scope);
    return {
      id: nextRuleIdRef.current++,
      field: field.value,
      operator: field.operators[0].value,
      value: '',
    };
  };

  const addRule = (groupId: number) => {
    setRule((current) => ({
      ...current,
      groups: current.groups.map((group) =>
        group.id === groupId
          ? { ...group, rules: [...group.rules, createRule(group.scope)] }
          : group,
      ),
    }));
  };

  const updateRule = <K extends keyof RuleRow>(
    groupId: number,
    ruleId: number,
    key: K,
    nextValue: RuleRow[K],
  ) => {
    if (key === 'value' && errors.rules) {
      setErrors((current) => ({ ...current, rules: undefined }));
    }
    setRule((current) => ({
      ...current,
      groups: current.groups.map((group) => {
        if (group.id !== groupId) return group;

        return {
          ...group,
          rules: group.rules.map((rule) => {
            if (rule.id !== ruleId) return rule;

            if (key === 'field') {
              const nextField = getFieldOption(nextValue as string);
              return {
                ...rule,
                field: nextField.value,
                operator: nextField.operators[0].value,
                value: '',
              };
            }

            if (key === 'operator') {
              const nextOperator = nextValue as string;
              // A list-file value is an uploaded file URL; it is meaningless
              // for other operators (and vice versa), so reset it when the
              // operator kind changes.
              const keepValue =
                isListFileOperator(nextOperator) ===
                isListFileOperator(rule.operator);
              return {
                ...rule,
                operator: nextOperator,
                value: keepValue ? rule.value : '',
              };
            }

            return { ...rule, [key]: nextValue };
          }),
        };
      }),
    }));
  };

  const removeRule = (groupId: number, ruleId: number) => {
    setRule((current) => ({
      ...current,
      groups: current.groups
        .map((group) =>
          group.id === groupId
            ? {
                ...group,
                rules: group.rules.filter((rule) => rule.id !== ruleId),
              }
            : group,
        )
        .filter((group) => group.rules.length > 0),
    }));
  };

  const setGroupMatchMode = (groupId: number, mode: PolicyMatchMode) => {
    setRule((current) => ({
      ...current,
      groups: current.groups.map((group) =>
        group.id === groupId ? { ...group, matchMode: mode } : group,
      ),
    }));
  };

  /**
   * Changes what a group checks. Conditions that already belong to the new
   * scope are kept; the rest are re-pointed at the scope's first field (their
   * old field does not exist there).
   */
  const setGroupScope = (
    groupId: number,
    scope: PolicyRuleScope,
    quantifier: PolicyRuleQuantifier,
  ) => {
    setRule((current) => ({
      ...current,
      groups: current.groups.map((group) => {
        if (group.id !== groupId) return group;
        if (group.scope === scope) return { ...group, quantifier };

        const fallback = firstFieldForScope(scope);
        const rules = group.rules.map((row) =>
          scopeOfField(row.field) === scope
            ? row
            : {
                ...row,
                field: fallback.value,
                operator: fallback.operators[0].value,
                value: '',
              },
        );
        return { ...group, scope, quantifier, rules };
      }),
    }));
  };

  const setRootMatchMode = (mode: PolicyMatchMode) => {
    setRule((current) => ({ ...current, matchMode: mode }));
  };

  const addGroup = () => {
    setRule((current) => ({
      ...current,
      groups: [
        ...current.groups,
        {
          id: nextGroupIdRef.current++,
          scope: 'page',
          quantifier: 'any',
          matchMode: 'all',
          rules: [createRule()],
        },
      ],
    }));
  };

  const removeGroup = (groupId: number) => {
    setRule((current) => ({
      ...current,
      groups: current.groups.filter((group) => group.id !== groupId),
    }));
  };

  const buildBody = (status: PolicyStatus): CreatePolicyBody => ({
    name: name.trim(),
    description: description.trim() || null,
    assetType,
    violationLabel,
    priority,
    teamId,
    domainScope,
    auditEntryIds: domainScope === 'selected-domains' ? includedDomainIds : [],
    rule: {
      matchMode: rule.matchMode,
      groups: rule.groups.map((group): PolicyRuleGroupBody => {
        const rules = group.rules.map((row) => ({
          field: row.field,
          operator: row.operator,
          value: row.value,
        }));
        return group.scope === 'page'
          ? { scope: 'page', matchMode: group.matchMode, rules }
          : {
              scope: group.scope,
              quantifier: group.quantifier,
              matchMode: group.matchMode,
              rules,
            };
      }),
    },
    status,
  });

  const submit = (status: PolicyStatus) => {
    if (isSubmitting) return;

    // Both "Save as draft" and the primary action validate against the schema
    // and surface inline errors instead of submitting an invalid policy.
    const result = policyFormSchema.safeParse({
      name,
      domainScope,
      includedDomains: includedDomainIds,
      groups: rule.groups,
    });

    if (!result.success) {
      const nextErrors: PolicyFormErrors = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0];
        if (field === 'name' && !nextErrors.name) {
          nextErrors.name = t(issue.message);
        } else if (field === 'includedDomains' && !nextErrors.includedDomains) {
          nextErrors.includedDomains = t(issue.message);
        } else if (field === 'groups' && !nextErrors.rules) {
          nextErrors.rules = t(issue.message);
        }
      }
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    onSubmit(buildBody(status));
  };

  return (
    <div className='eye:flex eye:flex-col eye:gap-m'>
      <section className='eye:flex eye:flex-col eye:gap-s eye:lg:flex-row eye:lg:items-start eye:lg:justify-between'>
        <div className='eye:max-w-[920px]'>
          <Button asChild variant='ghost' size='sm' className='eye:mb-xs'>
            <RouterLink to='/policies'>
              <ArrowLeft aria-hidden />
              {t('backToPolicies')}
            </RouterLink>
          </Button>

          <Typography tag='h1' variant='headline-xl'>
            {heading}
          </Typography>
          <Typography className='eye:mt-5xs eye:text-grey-120'>
            {pageDescription}
          </Typography>
        </div>

        <div className='eye:flex eye:flex-nowrap eye:items-center eye:gap-xs eye:self-start'>
          {canSaveAsDraft ? (
            <Button
              variant='outline'
              className='eye:shrink-0'
              disabled={isSubmitting}
              onClick={() => submit('draft')}
            >
              <Save aria-hidden />
              {t('saveAsDraft')}
            </Button>
          ) : null}
          <Button
            variant='yellow'
            className='eye:shrink-0'
            disabled={isSubmitting}
            onClick={() => submit('active')}
          >
            {submitLabel}
          </Button>
        </div>
      </section>

      <section className='eye:grid eye:grid-cols-1 eye:gap-s eye:lg:grid-cols-3'>
        <div className='eye:flex eye:flex-col eye:gap-s'>
          <SectionCard title={t('policySetup')}>
            <div className='eye:flex eye:flex-col eye:gap-s'>
              <div className='eye:flex eye:flex-col eye:gap-5xs'>
                <Input
                  label={t('policyName')}
                  size='sm'
                  required
                  aria-invalid={errors.name ? true : undefined}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    if (errors.name)
                      setErrors((current) => ({ ...current, name: undefined }));
                  }}
                />
                <FieldError>{errors.name}</FieldError>
              </div>

              <Textarea
                label={t('description')}
                size='sm'
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />

              <Select
                label={t('priority')}
                size='sm'
                placeholder={t('choosePriority')}
                value={priority}
                onValueChange={(value) => setPriority(value as PolicyPriority)}
              >
                <SelectItem value='low'>
                  <span className='eye:flex eye:items-center eye:gap-4xs'>
                    <PrioritySignal
                      priority='low'
                      className='eye:size-[16px]'
                    />
                    {t('priorityLow')}
                  </span>
                </SelectItem>
                <SelectItem value='medium'>
                  <span className='eye:flex eye:items-center eye:gap-4xs'>
                    <PrioritySignal
                      priority='medium'
                      className='eye:size-[16px]'
                    />
                    {t('priorityMedium')}
                  </span>
                </SelectItem>
                <SelectItem value='high'>
                  <span className='eye:flex eye:items-center eye:gap-4xs'>
                    <PrioritySignal
                      priority='high'
                      className='eye:size-[16px]'
                    />
                    {t('priorityHigh')}
                  </span>
                </SelectItem>
              </Select>

              {teams.length > 0 ? (
                <div className='eye:relative'>
                  <Select
                    label={t('teamOptional')}
                    size='sm'
                    value={teamId?.toString() ?? 'all-teams'}
                    onValueChange={(value) =>
                      setTeamId(value === 'all-teams' ? null : Number(value))
                    }
                    slotProps={{
                      trigger: {
                        className:
                          teamId !== null ? 'eye:pr-[56px]' : undefined,
                      },
                    }}
                  >
                    <SelectItem value='all-teams'>{t('noTeam')}</SelectItem>
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={String(team.id)}>
                        <span className='eye:flex eye:items-center eye:gap-2xs'>
                          <Avatar
                            style={{
                              backgroundColor: resolveTeamColor(team.color),
                            }}
                            className='eye:size-[24px] eye:shrink-0 eye:rounded-md eye:font-semibold eye:text-white'
                          >
                            {getInitials(team.name)}
                          </Avatar>
                          {team.name}
                        </span>
                      </SelectItem>
                    ))}
                  </Select>
                  {teamId !== null ? (
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon-xs'
                      className='eye:absolute eye:top-1/2 eye:right-[28px] eye:z-10 eye:-translate-y-1/2'
                      aria-label={t('clearSelectedTeam')}
                      onClick={() => setTeamId(null)}
                    >
                      <X aria-hidden />
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard title={t('scopeAndExecution')}>
            <div className='eye:flex eye:flex-col eye:gap-s'>
              <div className='eye:flex eye:flex-col eye:gap-s'>
                <Select
                  label={t('domainScope')}
                  size='sm'
                  placeholder={t('chooseScope')}
                  value={domainScope}
                  onValueChange={(value) =>
                    setDomainScope(value as PolicyDomainScope)
                  }
                >
                  <SelectItem value='all-domains' disabled={!canEditAllDomains}>
                    {t('allDomainsInAccount')}
                  </SelectItem>
                  <SelectItem value='selected-domains'>
                    {t('specificDomainsOnly')}
                  </SelectItem>
                </Select>
              </div>

              {domainScope === 'selected-domains' ? (
                <div className='eye:flex eye:flex-col eye:gap-5xs'>
                  <FormDomainSelect
                    label={t('includedDomains')}
                    placeholder={t('selectDomains')}
                    value={includedDomainIds}
                    invalid={Boolean(errors.includedDomains)}
                    onValueChange={(ids) => {
                      setIncludedDomainIds(ids);
                      if (errors.includedDomains)
                        setErrors((current) => ({
                          ...current,
                          includedDomains: undefined,
                        }));
                    }}
                  />
                  <FieldError>{errors.includedDomains}</FieldError>
                </div>
              ) : null}

              <Alert variant='info' title={t('domainSettingsTitle')}>
                {t('domainSettingsDescription')}{' '}
                <RouterLink
                  to='/report/settings'
                  className='eye:font-medium eye:underline eye:hover:no-underline'
                >
                  {t('reportSettings')}
                </RouterLink>
              </Alert>
            </div>
          </SectionCard>
        </div>

        <div className='eye:lg:col-span-2'>
          <SectionCard
            title={t('issueRule')}
            // A "required" policy inverts the match: the rule describes what a
            // page must have, so it is the *absence* of a match that is the
            // issue. See `policyValidator.ts` in the crawler.
            description={t(
              violationLabel === 'required'
                ? 'issueRuleDescriptionRequired'
                : 'issueRuleDescription',
            )}
          >
            <div className='eye:flex eye:flex-col eye:gap-3xs'>
              {errors.rules ? (
                <div className='eye:pb-3xs'>
                  <FieldError>{errors.rules}</FieldError>
                </div>
              ) : null}
              {rule.groups.map((group, groupIndex) => {
                const scoped = group.scope !== 'page';
                // Scoped groups always get the card chrome — the tinted box is
                // the visual "these conditions describe the same item" boundary.
                const grouped = rule.groups.length > 1 || scoped;
                const scopeNoun = scoped
                  ? (scopeOptionByValue.get(group.scope as PolicyEntityScope)
                      ?.noun ?? group.scope)
                  : '';

                const scopeSelect = !SHOW_SCOPE_SELECT ? null : (
                  <Select
                    label={t('check')}
                    size='sm'
                    value={
                      scoped ? `${group.quantifier}:${group.scope}` : 'page'
                    }
                    onValueChange={(value) => {
                      if (value === 'page') {
                        setGroupScope(group.id, 'page', 'any');
                        return;
                      }
                      const [quantifier, scope] = value.split(':');
                      setGroupScope(
                        group.id,
                        scope as PolicyRuleScope,
                        quantifier as PolicyRuleQuantifier,
                      );
                    }}
                  >
                    <SelectItem value='page'>{t('thisPage')}</SelectItem>
                    {scopeOptions.flatMap((scope) => [
                      <SelectItem
                        key={`any:${scope.value}`}
                        value={`any:${scope.value}`}
                      >
                        {t('anyScope', { scope: t(scope.noun) })}
                      </SelectItem>,
                      <SelectItem
                        key={`every:${scope.value}`}
                        value={`every:${scope.value}`}
                      >
                        {t('everyScope', { scope: t(scope.noun) })}
                      </SelectItem>,
                    ])}
                  </Select>
                );

                const rows = (
                  <div className='eye:flex eye:flex-col eye:gap-4xs'>
                    {group.rules.map((rule, index) => {
                      const fieldOption = getFieldOption(rule.field);

                      return (
                        <Fragment key={rule.id}>
                          <div className='eye:flex eye:flex-col eye:gap-xs eye:py-3xs'>
                            <ComboboxGrouped<RuleFieldItem>
                              label={t('whatToCheck')}
                              size='sm'
                              items={fieldItemsForScope(group.scope)}
                              value={
                                rule.field
                                  ? {
                                      value: fieldOption.value,
                                      label: t(fieldOption.label),
                                    }
                                  : null
                              }
                              onValueChange={(item) => {
                                if (item)
                                  updateRule(
                                    group.id,
                                    rule.id,
                                    'field',
                                    item.value,
                                  );
                              }}
                              itemToStringLabel={(item) => t(item.label)}
                              itemToStringValue={(item) => item.value}
                              renderGroupLabel={(group) =>
                                t(String(group.value))
                              }
                              slotProps={{
                                searchInput: {
                                  placeholder: t('typeToSearch'),
                                },
                                content: {
                                  // Size the popup to its content rather than
                                  // the trigger width, while staying at least
                                  // as wide as the trigger and within the
                                  // available viewport space.
                                  style: {
                                    width: 'max-content',
                                    minWidth: 'var(--anchor-width)',
                                    maxWidth: 'var(--available-width)',
                                  },
                                },
                              }}
                            />

                            <Select
                              label={t('should')}
                              size='sm'
                              value={rule.operator}
                              onValueChange={(value) =>
                                updateRule(group.id, rule.id, 'operator', value)
                              }
                            >
                              {fieldOption.operators.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {t(option.label)}
                                </SelectItem>
                              ))}
                            </Select>

                            {isListOperator(rule.operator) ||
                            isPhraseListOperator(rule.operator) ? (
                              <ChipInput
                                label={t('value')}
                                value={parseListValue(rule.value)}
                                placeholder={
                                  isPhraseListOperator(rule.operator)
                                    ? t('typePhraseAndPressEnter')
                                    : t('typeValueAndPressEnter')
                                }
                                onValueChange={(values) =>
                                  updateRule(
                                    group.id,
                                    rule.id,
                                    'value',
                                    values.join(','),
                                  )
                                }
                              />
                            ) : isListFileOperator(rule.operator) ? (
                              <ListFileValueInput
                                value={rule.value}
                                onValueChange={(value) =>
                                  updateRule(group.id, rule.id, 'value', value)
                                }
                              />
                            ) : (
                              <Input
                                label={t('value')}
                                size='sm'
                                value={rule.value}
                                placeholder={
                                  rule.operator === 'regex'
                                    ? getRegexPlaceholder(rule.field)
                                    : t(fieldOption.placeholder)
                                }
                                onChange={(event) =>
                                  updateRule(
                                    group.id,
                                    rule.id,
                                    'value',
                                    event.target.value,
                                  )
                                }
                              />
                            )}

                            <Button
                              variant='ghost'
                              size='icon'
                              className='eye:self-center'
                              aria-label={t('removeCondition', {
                                number: index + 1,
                              })}
                              onClick={() => removeRule(group.id, rule.id)}
                              disabled={totalRules === 1}
                            >
                              <Trash aria-hidden />
                            </Button>
                          </div>

                          {index < group.rules.length - 1 ? (
                            <MatchConnector
                              value={group.matchMode}
                              onChange={(mode) =>
                                setGroupMatchMode(group.id, mode)
                              }
                              ariaLabel={t('combineConditionsWith')}
                            />
                          ) : null}
                        </Fragment>
                      );
                    })}

                    <div className='eye:flex eye:justify-start'>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => addRule(group.id)}
                      >
                        <Plus aria-hidden />
                        {t('addCondition')}
                      </Button>
                    </div>
                  </div>
                );

                return (
                  <Fragment key={group.id}>
                    {grouped ? (
                      <div
                        className={
                          scoped
                            ? 'eye:flex eye:flex-col eye:gap-xs eye:rounded-md eye:border eye:border-blue-100 eye:bg-blue-25 eye:px-xs eye:py-2xs'
                            : 'eye:flex eye:flex-col eye:gap-xs eye:rounded-md eye:border eye:border-grey-20 eye:bg-grey-5 eye:px-xs eye:py-2xs'
                        }
                      >
                        <div
                          className={
                            scopeSelect
                              ? 'eye:flex eye:items-end eye:justify-between eye:gap-xs'
                              : 'eye:flex eye:items-center eye:justify-between eye:gap-xs'
                          }
                        >
                          <div className='eye:max-w-[240px] eye:flex-1'>
                            {scopeSelect ?? (
                              <Typography
                                variant='body-s-highlight'
                                className='eye:text-grey-120'
                              >
                                {scoped
                                  ? t(
                                      group.quantifier === 'every'
                                        ? 'everyScope'
                                        : 'anyScope',
                                      { scope: t(scopeNoun) },
                                    )
                                  : t('groupNumber', {
                                      number: groupIndex + 1,
                                    })}
                              </Typography>
                            )}
                          </div>
                          {rule.groups.length > 1 ? (
                            <Button
                              variant='ghost'
                              size='sm'
                              aria-label={t('removeGroup', {
                                number: groupIndex + 1,
                              })}
                              onClick={() => removeGroup(group.id)}
                            >
                              <Trash aria-hidden />
                              {t('removeGroup')}
                            </Button>
                          ) : null}
                        </div>
                        {rows}
                        {scoped ? (
                          <Typography
                            variant='body-s'
                            className='eye:text-grey-120'
                          >
                            {group.quantifier === 'any'
                              ? t('anyScopeMatchesDescription', {
                                  scope: t(scopeNoun),
                                  matchMode: t(
                                    group.matchMode === 'all' ? 'all' : 'any',
                                  ),
                                })
                              : t('everyScopeMatchesDescription', {
                                  scope: t(scopeNoun),
                                  matchMode: t(
                                    group.matchMode === 'all' ? 'all' : 'any',
                                  ),
                                })}
                          </Typography>
                        ) : null}
                      </div>
                    ) : scopeSelect ? (
                      <>
                        <div className='eye:max-w-[240px]'>{scopeSelect}</div>
                        {rows}
                      </>
                    ) : (
                      rows
                    )}

                    {groupIndex < rule.groups.length - 1 ? (
                      <MatchConnector
                        value={rule.matchMode}
                        onChange={setRootMatchMode}
                        ariaLabel={t('combineGroupsWith')}
                      />
                    ) : null}
                  </Fragment>
                );
              })}

              <div className='eye:flex eye:justify-start eye:pt-3xs'>
                <Button variant='ghost' size='sm' onClick={addGroup}>
                  <Plus aria-hidden />
                  {t('addGroup')}
                </Button>
              </div>
            </div>
          </SectionCard>
        </div>
      </section>
    </div>
  );
}

export default PolicyForm;
